import {useEffect, useMemo, useState} from 'react'
import {useLazyQuery} from '@apollo/client'
import {Button} from 'react-bootstrap'
import {defineMessages, useIntl} from 'react-intl'
import {toast} from 'react-toastify'

import {GET_GROUPS_PIPELINE, GET_MEMBERS_PIPELINE} from '../../../../gql/queries/pipelines'
import {
  IPipelineGroupsPipeline,
  IPipelineMemberNode,
  IPipelineMembersPipeline,
} from '../../../../types/users'
import {parseId} from '../../../../helpers'
import {useDisclourse} from '../../../../hooks/useDisclourse'
import {ModalAssignAgentsToPipeline} from '../../../leads/components/ModalAssignAgentsToPipeline'

interface ITabProps {
  /** ID global del pipeline (se parsea internamente). */
  pipelineId: string
  pipelineName: string
}

type TReviewerSort = 'name' | 'email'

const dash = '—'

const atM = defineMessages({
  groupsTitle: {
    id: 'leads.pipelines.edit.groupsTitle',
    defaultMessage: 'Groups that receive leads',
  },
  groupsHint: {
    id: 'leads.pipelines.edit.groupsHint',
    defaultMessage:
      'The users of these groups are the pool that receives the leads of this pipeline.',
  },
  groupsEmpty: {
    id: 'leads.pipelines.edit.groupsEmpty',
    defaultMessage: 'No groups linked. Nobody will receive leads automatically.',
  },
  reviewersTitle: {
    id: 'leads.pipelines.edit.reviewersTitle',
    defaultMessage: 'Reviewers of the pipeline',
  },
  reviewersHint: {
    id: 'leads.pipelines.edit.reviewersHint',
    defaultMessage:
      'Reviewers can see every lead of this pipeline, regardless of who it is assigned to. Being a reviewer does not make a user receive leads.',
  },
  reviewersEmpty: {
    id: 'leads.pipelines.edit.reviewersEmpty',
    defaultMessage: 'No reviewers assigned.',
  },
  manage: {id: 'leads.pipelines.edit.manage', defaultMessage: 'Manage'},
  colName: {id: 'common.field.name', defaultMessage: 'Name'},
  colEmail: {id: 'common.field.email', defaultMessage: 'Email'},
  colRole: {id: 'leads.pipelines.edit.colRole', defaultMessage: 'Role'},
  sortAsc: {id: 'leads.pipelines.edit.sortAsc', defaultMessage: 'Sort ascending'},
  sortDesc: {id: 'leads.pipelines.edit.sortDesc', defaultMessage: 'Sort descending'},
})

/** Lista los grupos vinculados al pipeline y delega la edición al modal existente. */
export const PipelineGroupsTab: React.FC<ITabProps> = ({pipelineId, pipelineName}) => {
  const intl = useIntl()
  const {isOpen, onClose, onOpen} = useDisclourse()

  const [gqlGetGroups, {data, loading, error, refetch}] = useLazyQuery<IPipelineGroupsPipeline>(
    GET_GROUPS_PIPELINE,
    {fetchPolicy: 'no-cache'}
  )

  useEffect(() => {
    gqlGetGroups({variables: {id: parseId(pipelineId)}})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pipelineId])

  useEffect(() => {
    if (error) toast.error(error.message)
  }, [error])

  const groups = data?.pipelineById?.groups?.edges ?? []

  return (
    <div className='policy-form'>
      <Header
        title={intl.formatMessage(atM.groupsTitle)}
        hint={intl.formatMessage(atM.groupsHint)}
        count={groups.length}
        onManage={onOpen}
        manageLabel={intl.formatMessage(atM.manage)}
      />

      {loading ? (
        <Loading />
      ) : groups.length === 0 ? (
        <EmptyState message={intl.formatMessage(atM.groupsEmpty)} />
      ) : (
        <div className='d-flex flex-wrap gap-2'>
          {groups.map(({node}) => (
            <div
              key={node.id}
              className='d-flex align-items-center gap-2 border rounded px-4 py-3'
              style={{minWidth: '220px'}}
            >
              <i className='bi bi-people fs-3 text-primary' />
              <span className='fw-semibold'>{node.name}</span>
            </div>
          ))}
        </div>
      )}

      {isOpen && (
        <ModalAssignAgentsToPipeline
          isOpen={isOpen}
          onClose={() => {
            // El modal existente no avisa de cambios, así que se recarga al cerrar.
            onClose()
            refetch()
          }}
          info={{pipelineId, pipelineName}}
          typeAssign='group'
        />
      )}
    </div>
  )
}

/** Lista los revisores del pipeline y delega la edición al modal existente. */
export const PipelineReviewersTab: React.FC<ITabProps> = ({pipelineId, pipelineName}) => {
  const intl = useIntl()
  const {isOpen, onClose, onOpen} = useDisclourse()

  const [gqlGetMembers, {data, loading, error, refetch}] = useLazyQuery<IPipelineMembersPipeline>(
    GET_MEMBERS_PIPELINE,
    {fetchPolicy: 'no-cache'}
  )

  useEffect(() => {
    gqlGetMembers({variables: {id: parseId(pipelineId)}})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pipelineId])

  useEffect(() => {
    if (error) toast.error(error.message)
  }, [error])

  const [sortBy, setSortBy] = useState<TReviewerSort>('name')
  const [sortDesc, setSortDesc] = useState(false)

  const reviewers = useMemo(() => data?.pipelineById?.users?.edges ?? [], [data])

  const sorted = useMemo(() => {
    const key = (node: IPipelineMemberNode) =>
      sortBy === 'email'
        ? node.email ?? ''
        : `${node.firstName ?? ''} ${node.lastName ?? ''}`.trim()
    // localeCompare para que acentos y ñ ordenen como espera un hispanohablante.
    return [...reviewers].sort((a, b) => {
      const cmp = key(a.node).localeCompare(key(b.node), undefined, {sensitivity: 'base'})
      return sortDesc ? -cmp : cmp
    })
  }, [reviewers, sortBy, sortDesc])

  const toggleSort = (column: TReviewerSort) => {
    if (column === sortBy) {
      setSortDesc((prev) => !prev)
      return
    }
    setSortBy(column)
    setSortDesc(false)
  }

  /** Qué haría el próximo click, para el tooltip del encabezado. */
  const nextSortIsDesc = (column: TReviewerSort) => column === sortBy && !sortDesc

  return (
    <div className='policy-form'>
      <Header
        title={intl.formatMessage(atM.reviewersTitle)}
        hint={intl.formatMessage(atM.reviewersHint)}
        count={reviewers.length}
        onManage={onOpen}
        manageLabel={intl.formatMessage(atM.manage)}
      />

      {loading ? (
        <Loading />
      ) : reviewers.length === 0 ? (
        <EmptyState message={intl.formatMessage(atM.reviewersEmpty)} />
      ) : (
        <div className='table-responsive'>
          <table className='table table-row-dashed align-middle gs-0 gy-2 mb-0'>
            <thead>
              <tr className='fw-bold text-muted fs-8 text-uppercase'>
                <SortableTh
                  label={intl.formatMessage(atM.colName)}
                  column='name'
                  sortBy={sortBy}
                  sortDesc={sortDesc}
                  onSort={toggleSort}
                  title={intl.formatMessage(nextSortIsDesc('name') ? atM.sortDesc : atM.sortAsc)}
                />
                <SortableTh
                  label={intl.formatMessage(atM.colEmail)}
                  column='email'
                  sortBy={sortBy}
                  sortDesc={sortDesc}
                  onSort={toggleSort}
                  title={intl.formatMessage(nextSortIsDesc('email') ? atM.sortDesc : atM.sortAsc)}
                />
                <th>{intl.formatMessage(atM.colRole)}</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(({node}) => (
                <tr key={node.id}>
                  <td className='fw-semibold'>
                    {node.firstName} {node.lastName}
                  </td>
                  <td className='text-muted'>{node.email}</td>
                  <td>
                    {node.rol ? (
                      <span className='badge badge-light-primary'>{node.rol}</span>
                    ) : (
                      <span className='text-muted'>{dash}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isOpen && (
        <ModalAssignAgentsToPipeline
          isOpen={isOpen}
          onClose={() => {
            onClose()
            refetch()
          }}
          info={{pipelineId, pipelineName}}
          typeAssign='user'
        />
      )}
    </div>
  )
}

/** Encabezado de columna que alterna asc/desc al hacer click. */
const SortableTh: React.FC<{
  label: string
  column: TReviewerSort
  sortBy: TReviewerSort
  sortDesc: boolean
  onSort: (column: TReviewerSort) => void
  title: string
}> = ({label, column, sortBy, sortDesc, onSort, title}) => {
  const active = sortBy === column
  return (
    <th role='button' className='user-select-none' title={title} onClick={() => onSort(column)}>
      <span className='d-inline-flex align-items-center gap-1'>
        {label}
        <i
          className={`bi ${active ? (sortDesc ? 'bi-sort-down' : 'bi-sort-up') : 'bi-dash'} ${
            active ? 'text-primary' : 'text-muted opacity-50'
          }`}
        />
      </span>
    </th>
  )
}

const Header: React.FC<{
  title: string
  hint: string
  count: number
  onManage: () => void
  manageLabel: string
}> = ({title, hint, count, onManage, manageLabel}) => (
  <div className='d-flex align-items-start justify-content-between gap-3 mb-4'>
    <div>
      <h5 className='mb-1 d-flex align-items-center gap-2'>
        {title}
        <span className='badge badge-light-primary'>{count}</span>
      </h5>
      <p className='text-muted fs-8 mb-0'>{hint}</p>
    </div>
    <Button
      size='sm'
      variant='outline-primary'
      className='d-flex align-items-center gap-2 flex-shrink-0'
      onClick={onManage}
    >
      <i className='bi bi-pencil-square' />
      {manageLabel}
    </Button>
  </div>
)

const Loading = () => (
  <div className='text-muted py-5'>
    <span className='spinner-border spinner-border-sm me-2' />
  </div>
)

const EmptyState: React.FC<{message: string}> = ({message}) => (
  <div className='text-muted text-center border border-dashed rounded py-6'>
    <i className='bi bi-exclamation-circle me-2' />
    {message}
  </div>
)
